import * as React from "react"

// A `useState` that mirrors its value into `localStorage` under `key`, so the
// choice survives navigation and full reloads.
//
// SSR-safe: the first client render always uses `initial` (matching the server
// render) and the stored value is restored in an effect after mount, avoiding
// hydration mismatches. All storage access is wrapped in try/catch — private
// mode or blocked storage degrades to plain in-memory state.
//
// For non-JSON values (e.g. a Set) pass custom `serialize` / `deserialize`.
export function useStoredState<T>(
  key: string,
  initial: T,
  opts?: {
    serialize?: (value: T) => string
    deserialize?: (raw: string) => T
  },
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const serialize = opts?.serialize ?? ((v: T) => JSON.stringify(v))
  const deserialize = opts?.deserialize ?? ((raw: string) => JSON.parse(raw) as T)

  const [value, setValue] = React.useState<T>(initial)

  // Restore once on mount. Runs before the persist effect below, so it reads
  // the previously-saved value rather than the freshly-written `initial`.
  React.useEffect(() => {
    if (typeof window === "undefined") return
    try {
      const raw = window.localStorage.getItem(key)
      if (raw != null) setValue(deserialize(raw))
    } catch {
      // ignore malformed / unavailable storage
    }
    // Restore only on first mount, keyed by storage key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  React.useEffect(() => {
    if (typeof window === "undefined") return
    try {
      window.localStorage.setItem(key, serialize(value))
    } catch {
      // non-fatal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value])

  return [value, setValue]
}
